// Inline <style>/class CSS into element style="" attributes so author-written HTML survives
// email clients (Gmail/Outlook strip or ignore <style>). Runs client-side; juice is loaded
// lazily so it never bloats the main bundle, and there's a DOM-based fallback if juice can't
// be bundled in this environment. Media queries / pseudo-selectors are preserved (left in a
// <style> block) since they can't be inlined.

let juiceMod: any | null | undefined

async function tryJuice(html: string): Promise<string | null> {
  if (juiceMod === null) return null // previously failed to load
  try {
    if (!juiceMod) juiceMod = (await import('juice')).default || (await import('juice'))
    return juiceMod(html, { preserveMediaQueries: true, preserveFontFaces: true, preserveImportant: true })
  } catch (err) {
    console.warn('juice unavailable, using DOM inliner fallback:', err)
    juiceMod = null
    return null
  }
}

/** Browser-native fallback: parse <style> rules via the live CSSOM and apply them inline. */
function domInline(html: string): string {
  if (typeof document === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const styleTags = Array.from(doc.querySelectorAll('style'))
  if (!styleTags.length) return html

  const css = styleTags.map((t) => t.textContent || '').join('\n')

  // Use a real <style> in the host document to get parsed CSSOM rules.
  const host = document.createElement('style')
  host.textContent = css
  document.head.appendChild(host)
  const sheet = host.sheet as CSSStyleSheet | null
  const leftover: string[] = []

  try {
    const rules = sheet ? Array.from(sheet.cssRules) : []
    for (const rule of rules) {
      if (!(rule instanceof CSSStyleRule)) {
        // @media / @font-face / keyframes — cannot inline, keep in a <style>.
        leftover.push(rule.cssText)
        continue
      }
      // Pseudo-classes/elements (:hover, ::before) can't be inlined either.
      if (/[:]{1,2}[a-z-]+(\(|\b)/i.test(rule.selectorText) && !/^\s*[.#a-z0-9\[\]="'\-\s>,+~]*$/i.test(rule.selectorText)) {
        leftover.push(rule.cssText)
        continue
      }
      let targets: Element[] = []
      try {
        targets = Array.from(doc.querySelectorAll(rule.selectorText))
      } catch {
        leftover.push(rule.cssText)
        continue
      }
      for (const el of targets) {
        const existing = el.getAttribute('style') || ''
        // Existing inline styles win — prepend rule styles.
        const merged = `${rule.style.cssText}${existing && !existing.trim().endsWith(';') ? ';' : ''}${existing}`
        el.setAttribute('style', merged)
      }
    }
  } finally {
    host.remove()
  }

  // Replace <style> tags: drop inlined rules, keep only un-inlinable leftovers.
  styleTags.forEach((t, i) => {
    if (i === 0 && leftover.length) t.textContent = leftover.join('\n')
    else t.remove()
  })

  return doc.body ? doc.body.innerHTML : html
}

export function useCssInline() {
  /** Inline CSS in an HTML fragment/document. No-op when there's no <style> to inline. */
  async function inlineCss(html: string): Promise<string> {
    if (!html || !html.includes('<style')) return html
    const viaJuice = await tryJuice(html)
    if (viaJuice != null) return viaJuice
    try {
      return domInline(html)
    } catch (err) {
      console.error('CSS inline failed:', err)
      return html
    }
  }

  return { inlineCss }
}
